// ProfileEdit.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import SidebarHeader from "../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function ProfileEdit() {
    const navigate = useNavigate();
    const [fetching, setFetching] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobile: "",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const result = await apiCall(
                "profile-update",
                {
                    name: formData.name,
                    email: formData.email,
                },
                true
            );

            if (result.status) {
                toast.success("Profile updated successfully!");
                // Update local storage
                localStorage.setItem("username", formData.name);
                localStorage.setItem("email", formData.email);
                setTimeout(() => {
                    navigate("/profile");
                }, 1500);
            } else {
                toast.error(result.message || "An error occurred. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const result = await apiCall(
                    "profile-fetch",
                    {},
                    true
                );

                if (result.status && result.profile) {
                    setFormData({
                        name: result.profile.name || "",
                        email: result.profile.email || "",
                        mobile: result.profile.mobile || "",
                    });
                } else {
                    toast.error(result.message || "An error occurred. Please try again.");
                }
            } catch (error) {
                toast.error("Network error. Please try again.");
            } finally {
                setFetching(false);
            }
        }
        fetchProfile();
    }, [])

    if (fetching) {
        return (
            <SidebarHeader>
                <div className="flex items-center gap-3 pb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                    >
                        <FiArrowLeft className="text-xl" />
                    </button>
                    <h1 className="text-lg font-semibold text-gray-800">
                        Profile Update
                    </h1>
                </div>
                <div className="max-w-7xl mx-auto mb-16">
                    <div className="text-center py-12">
                        <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${COLORS.primaryColor}33`, borderTopColor: COLORS.primaryColor }} />
                        <div className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
                            Loading profile...
                        </div>
                    </div>
                </div>
            </SidebarHeader>
        );
    }

    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    Profile Update
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16">
                {/* Profile Image Section */}
                <div className="flex flex-col items-center mb-8">
                    <div className="relative w-[84px] h-[84px]">
                        <div
                            className="w-[84px] h-[84px] rounded-full flex items-center justify-center text-4xl font-bold"
                            style={{
                                background: COLORS.primaryColor,
                                color: COLORS.textOnPrimary,
                            }}
                        >
                            {formData.name ? formData.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <button
                            className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                            style={{
                                background: COLORS.bgColor,
                                border: `1.5px solid ${COLORS.borderColor}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={COLORS.primaryColor}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form Section */}
                <div
                    className="rounded-2xl p-6 pb-4 mb-6"
                    style={{
                        border: `1px solid ${COLORS.borderColor}`,
                        borderRadius: COLORS.borderRadius,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Full Name */}
                            <div>
                                <label
                                    className="block text-[13px] font-semibold mb-1.5"
                                    style={{
                                        color: COLORS.textPrimary,
                                    }}
                                >
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3.5 py-3 text-sm outline-none transition-colors duration-200 rounded-xl"
                                    style={{
                                        border: `1px solid ${COLORS.borderColor}`,
                                        borderRadius: COLORS.borderRadius,
                                        background: COLORS.bgColor,
                                        color: COLORS.textPrimary,
                                    }}
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    onFocus={(e) => (e.target.style.borderColor = COLORS.primaryColor)}
                                    onBlur={(e) => (e.target.style.borderColor = COLORS.borderColor)}
                                    required
                                />
                            </div>

                            {/* Email Address */}
                            <div>
                                <label
                                    className="block text-[13px] font-semibold mb-1.5"
                                    style={{
                                        color: COLORS.textPrimary,
                                    }}
                                >
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    className="w-full px-3.5 py-3 text-sm outline-none transition-colors duration-200 rounded-xl"
                                    style={{
                                        border: `1px solid ${COLORS.borderColor}`,
                                        borderRadius: COLORS.borderRadius,
                                        background: COLORS.bgColor,
                                        color: COLORS.textPrimary,
                                    }}
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    onFocus={(e) => (e.target.style.borderColor = COLORS.primaryColor)}
                                    onBlur={(e) => (e.target.style.borderColor = COLORS.borderColor)}
                                    required
                                />
                            </div>

                            {/* Mobile Number (Read Only - Not Editable) */}
                            <div>
                                <label
                                    className="block text-[13px] font-semibold mb-1.5"
                                    style={{
                                        color: COLORS.textPrimary,
                                    }}
                                >
                                    Mobile Number
                                </label>
                                <input
                                    type="tel"
                                    className="w-full px-3.5 py-3 text-sm rounded-xl cursor-not-allowed"
                                    style={{
                                        border: `1px solid ${COLORS.borderColor}`,
                                        borderRadius: COLORS.borderRadius,
                                        background: COLORS.borderColor,
                                        color: COLORS.textSecondary,
                                    }}
                                    value={formData.mobile}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <button
                                type="submit"
                                className="w-full h-12 border-none rounded-lg text-[15px] font-bold cursor-pointer mt-2 transition-all duration-200 active:scale-98"
                                style={{
                                    background: COLORS.primaryColor,
                                    color: COLORS.textOnPrimary,
                                    borderRadius: COLORS.borderRadius,
                                    boxShadow: `0 4px 12px ${COLORS.primaryColor}33`,
                                }}
                            >
                                Update Profile
                            </button>
                        </div>
                    </form>
                </div>

                <Toaster />

                <style>
                    {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
                </style>
            </div>
        </SidebarHeader>
    );
}

export default ProfileEdit;